import {memo} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {body14Bold, body14Normal, body16Bold} from '~/shared/theme/typography';
import InfoIcon from '~/assets/images/icons/circle-info.svg?react';
import ErrorIcon from '~/assets/images/icons/close-circle-filled.svg?react';

import Button from '../Button';
import ToggleSwitch from '../ToggleSwitch';

export enum AutoCreditVariant {
  DEFAULT = 'default',
  COMPACT = 'compact',
}
export interface AutoCreditViewProps {
  variant: AutoCreditVariant;
  isActive: boolean;
  onInfoButtonClick: () => void;
  handleToggleClick: (newActiveState: boolean) => void;
  className?: string;
  backgroundColor?: string;
  isError?: boolean;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  background-color: transparent;
  overflow: visible;
`;

const MainContent = styled.div<{backgroundColor?: string}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 56px;
  max-height: 76px;
  padding: 16px;
  box-shadow: ${({theme}) => theme.shadows.elevationCard};
  border-radius: 12px;
  background-color: ${({theme, backgroundColor}) => backgroundColor || theme.colors.surface};
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  ${flipOnLTR`
    margin-right: 4px;
  `}
`;

const StyledInfoIcon = styled(InfoIcon).attrs({
  viewBox: '0 0 18 18',
  width: '14px',
  height: '14px',
})`
`;

const Title = styled.span<{variant?: AutoCreditVariant}>`
  ${body14Bold}
  line-height: 20px;
  color: ${({theme}) => theme.colors.contentDefault};
  ${({variant}) => variant === AutoCreditVariant.DEFAULT && `
    ${body16Bold}
  `}
`;

const SubTitle = styled.span`
  ${body14Normal}
  color: ${({theme}) => theme.colors.contentInteractiveSubdued};
  line-height: 20px;
  margin-top: 2px;
`;

const ErrorMessageContainer = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  line-height: 20px;
`;

const ErrorMessage = styled.p`
  ${body14Bold}
  color: ${({theme}) => theme.colors.error};
`;

const StyledErrorIcon = styled(ErrorIcon).attrs({viewBox: '0 0 20 20'})`
  width: 16px;
  height: 16px;
  ${flipOnLTR`
    margin-left: 6px;
  `}
  path {
    fill: ${({theme}) => theme.colors.error};
  }
`;

const AutoCreditView = (({variant, onInfoButtonClick, isActive, handleToggleClick, isError, backgroundColor, className}: AutoCreditViewProps) => {
  const {t} = getLocalizationService();

  return (
    <Root className={className}>
      <MainContent
        backgroundColor={backgroundColor}
        data-test-id="autoCredit"
      >
        <DescriptionContainer>
          <TextContainer>
            <Title variant={variant}>
              {t('add_credit_automatically')}
            </Title>
            {variant === AutoCreditVariant.DEFAULT && (
            <SubTitle>
              {t('add_credit_automatically_description')}
            </SubTitle>
          )}
          </TextContainer>
          {variant === AutoCreditVariant.COMPACT && (
            <InfoButton onClick={onInfoButtonClick}>
              <StyledInfoIcon />
            </InfoButton>
          )}
        </DescriptionContainer>
        <ToggleSwitch
          isActive={isActive}
          onClick={handleToggleClick}
          dataTestId="autoCreditToggle"
        />
      </MainContent>
      {isError && (
        <ErrorMessageContainer>
          <StyledErrorIcon />
          <ErrorMessage>
            {t('something_went_wrong_try_again_later')}
          </ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
});

export default memo(AutoCreditView);
